import {
  ChangeDetectorRef,
  Component, EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import {ReflectorService, SystemKeyRawValue} from "../../services/reflector.service";
import {MatPaginatorModule, PageEvent} from "@angular/material/paginator";
import {NgClassType, SrsNodeBaseDef} from "../../model/common.model";
import {
  DatePipe,
  JsonPipe,
  NgClass,
  NgForOf,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgSwitchDefault,
  NgTemplateOutlet
} from "@angular/common";

export interface SrsDataTableState {
  pageIndex: number
  pageSize: number
  pageCount: number
  pageItems: any[]
  allItems: any[]
}

export interface SrsDataTableColumnDef extends SrsNodeBaseDef {
  renderTemplate?: TemplateRef<{
    index: number,
    rowItem: any,
    columnKey: string | number,
    columnItem: any }>
}

export type SrsDataTableRowFn<T> =  (rowItem: any, index: number, context?: any) => T

export interface SrsDataTableOptions {
  tableNgClass?: NgClassType
  headingNgClass?: NgClassType
  rowNgClass?: NgClassType | SrsDataTableRowFn<NgClassType>
  cellNgClass?: NgClassType
  context?: any
}

export type SrsDataTableColumnOptions = { [id in string | number]: SrsDataTableColumnDef }

@Component({
  selector: 'data-table',
  templateUrl: './data-table.component.html',
  standalone: true,
  imports: [
    MatPaginatorModule,
    NgIf,
    NgClass,
    NgForOf,
    NgTemplateOutlet,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    JsonPipe,
    DatePipe
  ]
})
export class DataTableComponent implements OnInit, OnChanges {

  @Input() items?: any[]
  @Input() columnOptions:SrsDataTableColumnOptions = {
    '$$raw': {
      heading: 'Raw Result'
    }
  }
  @Input() columnHiddenFn: (columnName: string, context?: any) => boolean = (_) => { return false }
  @Input() options: SrsDataTableOptions = {}
  @Input() manualReloadToken?: string

  @Output() rowClick = new EventEmitter<{index: number, rowItem: any}>()
  @Output() cellClick = new EventEmitter<{index: number, rowItem: any, cellKey: string | number, cellItem: any}>()

  state: SrsDataTableState = {
    pageIndex: -1,
    pageSize: 20,
    allItems: [],
    pageCount: -1,
    pageItems: []
  }

  allColumnKeys: string[] = []
  renderColumnKeys: string[] = []
  renderColumnHeaders: string [] = []

  constructor(
    readonly reflector: ReflectorService,
    readonly cdr: ChangeDetectorRef
  ) { }

  getRowCssClass(rowItem: any, index: number): NgClassType {
    let result: {
      [klass: string]: any;
    } = {}

    if (this.rowClick.observed) {
      result['click-able'] = true
    }

    if (this.options.rowNgClass) {
      let rowResult: NgClassType
      if (this.reflector.isFunction(this.options.rowNgClass)) {
        const fn =  this.options.rowNgClass as SrsDataTableRowFn<NgClassType>
        rowResult = fn(rowItem, index, this.options.context)
      }
      else {
        rowResult = this.options.rowNgClass
      }
      if (this.reflector.isString(rowResult)) {
        result[rowResult as string] = true
      }
      else if (this.reflector.isArray(rowResult)) {
        const arr: string[] = rowResult as string[]
        arr.forEach(rr => {
          result[rr] =  true
        })
      }
      else if (this.reflector.isComposite(rowResult)) {
        result = Object.assign(result, rowResult)
      }
    }
    return result
  }

  getCellCssClass(cellKey: string) {
    let resultDictionary: { [id in string] : boolean } =
      { 'click-able': this.rowClick.observed || this.cellClick.observed }

    if (this.options.cellNgClass) {
      if (this.reflector.isString(this.options.cellNgClass)) {
        const classString = this.options.cellNgClass as string
        resultDictionary[classString] = true
      }
      else if (this.reflector.isArray(this.options.cellNgClass)) {
        (this.options.cellNgClass as string[]).forEach(classString => {
          resultDictionary[classString] = true
        })
      }
      else if (this.reflector.isComposite(this.options.cellNgClass)) {
        resultDictionary = Object.assign(resultDictionary, this.options.cellNgClass) as  { [id in string] : boolean }
      }
    }

    if (cellKey in this.columnOptions) {
      const outerNgClass = this.columnOptions[cellKey].outerNgClass
      if (this.reflector.isString(outerNgClass)) {
        const classString = outerNgClass as string
        resultDictionary[classString] = true
      }
      else if (this.reflector.isArray(outerNgClass)) {
        (outerNgClass as string[]).forEach(classString => {
          resultDictionary[classString] = true
        })
      }
      else if (this.reflector.isComposite(outerNgClass)) {
        resultDictionary = Object.assign(resultDictionary, outerNgClass) as  { [id in string] : boolean }
      }
    }

    return resultDictionary
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('items' in changes) {
      this.invalidateState()
    }
    else if ('manualReloadToken' in changes) {
      this.invalidateState()
    }
    else if ('columnOptions' in changes) {
      console.warn('xxxxxxx')
      this.invalidateState()
    }
  }

  getCellTemplateRef(columnKey: string): TemplateRef<{ rowItem: any, columnKey: string | number, columnItem: any }> | undefined {
    if (columnKey in this.columnOptions) {
      return this.columnOptions[columnKey].renderTemplate
    }
    return undefined
  }

  getCellValue(item: any, columnKey: string): any {
    if (this.reflector.isComposite(item)) {
      return item[columnKey]
    }
    else if (this.isRawHeader(columnKey)) {
      return item
    }
    return null
  }

  isRawHeader(columnKey: string): boolean {
    return columnKey === SystemKeyRawValue
  }

  isRawValue(target: any): boolean {
    return !this.reflector.isComposite(target)
  }

  protected invalidateState() {
    const pageSize = 20
    const allItems = this.items || []
    const dtState: SrsDataTableState = {
      pageIndex: 0,
      pageSize,
      pageItems: [],
      pageCount: -1,
      allItems
    }
    dtState.pageCount = Math.ceil(allItems.length/ pageSize)
    dtState.pageItems = allItems.length > pageSize
      ? allItems.slice(0, pageSize)
      : allItems
    this.state = dtState
    this.invalidateColumns()
    this.cdr.detectChanges()
  }

  protected invalidateColumns() {
    if (this.state.pageItems) {
      const columnNames: string[] = Object.getOwnPropertyNames(this.columnOptions)
        .filter(c => !this.columnOptions[c].hidden)
      let nonCompositeCount = 0
      let compositeCount = 0
      this.state.pageItems.forEach(item => {
        if (this.reflector.isComposite(item)) {
          compositeCount++
          Object.getOwnPropertyNames(item).sort().forEach(k => {
            if (!columnNames.includes(k)) {
              columnNames.push(k)
            }
          })
        }
        else {
          nonCompositeCount++
        }
      })
      if (nonCompositeCount > 0 && !(columnNames.includes(SystemKeyRawValue))) {
        columnNames.push(SystemKeyRawValue)
      }
      this.allColumnKeys = columnNames.sort()
      this.renderColumnKeys = this.allColumnKeys
        .filter((c) => {
          return !this.columnHiddenFn(c, this.options.context) && (!(c in this.columnOptions) || !this.columnOptions[c].hidden)
        })
        .sort((a: string, b: string) => {
          const aOrder = a in this.columnOptions ? (this.columnOptions[a].order || 0) : 0
          const bOrder = b in this.columnOptions ? (this.columnOptions[b].order || 0) : 0
          return aOrder - bOrder
        })

      if (nonCompositeCount === 0 && compositeCount > 0) {
        const matchIndex = this.allColumnKeys.findIndex(x => x === SystemKeyRawValue)
        if (matchIndex >= 0) {
          delete this.allColumnKeys[matchIndex]
          delete this.renderColumnKeys[matchIndex]
        }
      }

      this.renderColumnHeaders = this.renderColumnKeys.map(c => {
        if (c in this.columnOptions) {
          return this.columnOptions[c].heading || c
        }
        return c
      })
    }
  }

  changePage(index: number) {
    if (this.state.pageCount > index && index >= 0) {
      const pageSize = this.state.pageSize
      const startIndex = index * pageSize
      this.state.pageItems = this.state.allItems.slice(startIndex, startIndex + pageSize)
      this.state.pageIndex = index
      this.invalidateColumns()
      this.cdr.detectChanges()
    }
  }

  handlePageEvent(event: PageEvent) {
    this.changePage(event.pageIndex)
  }
}
